class SplittedShipmentPrompt extends HTMLElement {
  // The form element this component is associated with
  form = null
  // The original submitter button that triggered the checkout form submission
  submitter = null
  // Where the content will be rendered (is appended to body to avoid z-index issues)
  renderContainer = null

  // Callbacks to unbind event listeners
  unbindCallbacks = []
  unbindCheckoutIntercept = null
  unbindEscapeListener = null

  // Data to be rendered
  data = {
    loading: false,
    show: false,
    lastEstimatedShipmentDate: null,
    selectedShippingOption: "combined",
  }

  // Lifecycle method called when the component is added to the DOM
  connectedCallback() {
    // Search for render container to remove it
    const staledRenderContainer = document.body.querySelector('[data-component="splitted-shipment-prompt"]')
    if (staledRenderContainer) {
      staledRenderContainer.remove()
    }

    const formActionToIntercept = this.getAttribute("data-form-action")
    if (!formActionToIntercept) {
      console.warn(
        'The <splitted-shipment-prompt> component requires a "data-form-action" attribute to function properly.'
      )
      return
    }

    const closestForm = this.closest(`form[action="${formActionToIntercept}"]`)
    if (!closestForm) {
      console.warn(
        `The <splitted-shipment-prompt> component must be placed inside a form with action="${formActionToIntercept}".`
      )
      return
    }

    const isUnderCartForm = closestForm.contains(this)
    if (!isUnderCartForm) {
      console.warn("The <splitted-shipment-prompt> component must be placed inside the cart form.")
      return
    }

    this.form = closestForm

    const handleCheckoutFormSubmission = this.handleCheckoutFormSubmission.bind(this)
    closestForm.addEventListener("submit", handleCheckoutFormSubmission)
    this.unbindCheckoutIntercept = () => {
      closestForm.removeEventListener("submit", handleCheckoutFormSubmission)
    }

    this.renderContainer = document.createElement("div")
    this.renderContainer.dataset.component = "splitted-shipment-prompt"
    document.body.appendChild(this.renderContainer)

    // Initial render
    this.render()
  }

  // Lifecycle method called when the component is removed from the DOM
  disconnectedCallback() {
    this.unbindEvents()
    this.unbindCheckoutIntercept?.()
    this.unbindEscapeListener?.()
    this.isInitialized = false
  }

  handleEscapeKey(event) {
    if (event.key === "Escape" && this.data.show) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      this.handleClose(event)
    }
  }

  bindEscapeListener() {
    if (this.unbindEscapeListener) return

    const handler = this.handleEscapeKey.bind(this)
    // Use capture phase to intercept before other listeners
    document.addEventListener("keydown", handler, true)
    this.unbindEscapeListener = () => {
      document.removeEventListener("keydown", handler, true)
    }
  }

  unbindEscapeListenerMethod() {
    this.unbindEscapeListener?.()
    this.unbindEscapeListener = null
  }

  proceedToCheckout(submitter) {
    this.unbindCheckoutIntercept()
    queueMicrotask(() => this.form.requestSubmit(submitter))
  }

  async handleCheckoutFormSubmission(event) {
    const submitter = event.submitter

    // If preorder config is not enabled or completely missing, let the native submit proceed
    if (!window.ApparellCustomApp?.PreOrder?.enabled) {
      return
    }

    try {
      event.preventDefault()

      const { nonPreorderItems, preorderGroups } = await window.ApparellCustomApp.PreOrder.getPreorderGroups()
      const hasNonPreorderItems = nonPreorderItems.length > 0
      const hasPreorderItems = preorderGroups.length > 0
      const shouldShowPrompt =
        (hasNonPreorderItems && hasPreorderItems) || (!hasNonPreorderItems && preorderGroups.length > 1)

      if (shouldShowPrompt) {
        const latestPreorderGroup = preorderGroups.sort((a, b) => new Date(b.date) - new Date(a.date))[0]

        const lastestPreorderDate = new Date(latestPreorderGroup.date)
        // Undefined locale will use the user's browser locale
        const formattedDate = lastestPreorderDate.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })

        this.data.lastEstimatedShipmentDate = formattedDate

        this.submitter = submitter
        this.data.show = true
        this.bindEscapeListener()
        this.render()
        return
      }

      this.proceedToCheckout(submitter)
    } catch (error) {
      // In case of error, proceed with normal submission
      console.error("Error in splitted shipment prompt:", error)
      this.proceedToCheckout(submitter)
    }
  }

  // Placeholder method for handling simulated loading
  async handleConfirm(event) {
    try {
      event.preventDefault()

      this.data.loading = true
      this.render()

      switch (this.data.selectedShippingOption) {
        case "combined":
          await window.ApparellCustomApp.PreOrder.removePreorderPlansFromCart()

          break
        case "split":
          await window.ApparellCustomApp.PreOrder.addPreorderPlansToCart()

          break
        default:
          throw new Error(
            `Invalid shipping option ${this.data.selectedShippingOption} selected in splitted shipment prompt.`
          )
      }

      this.proceedToCheckout(this.submitter)
    } catch (error) {
      console.error("Error handling splitted shipment confirmation:", error)
      // In case of error, proceed with normal submission
      this.proceedToCheckout(this.submitter)
    }
  }

  // Handle close button click
  handleClose(event) {
    event.preventDefault()
    this.data.show = false
    this.unbindEscapeListenerMethod()
    this.render()
  }

  handleOptionSelect(event) {
    event.preventDefault()

    const optionKey = event.currentTarget?.getAttribute("data-option-key")
    if (!optionKey || optionKey === this.data.selectedShippingOption) {
      return
    }

    this.data.selectedShippingOption = optionKey
    this.render()
  }

  handleBackdropClick(event) {
    const backdrop = this.renderContainer.querySelector(".splitted-shipment-prompt__backdrop")
    const target = event.target

    if (target === backdrop) {
      this.handleClose(event)
    }
  }

  // Unbind all event listeners
  unbindEvents() {
    this.unbindCallbacks.forEach((unbind) => unbind())
    this.unbindCallbacks = []
  }

  /**
   * Helper method to register an event listener and store its unbind callback
   * to be safely removed later.
   */
  registerEventListener(element, eventType, handler) {
    element.addEventListener(eventType, handler)
    this.unbindCallbacks.push(() => {
      element.removeEventListener(eventType, handler)
    })
  }

  // Bind event listeners
  bindEvents() {
    const root = this.renderContainer
    const confirmButton = root.querySelector("#confirm")
    if (confirmButton) {
      this.registerEventListener(confirmButton, "click", this.handleConfirm.bind(this))
    }

    const closeButton = root.querySelector(".splitted-shipment-prompt__close")
    if (closeButton) {
      this.registerEventListener(closeButton, "click", this.handleClose.bind(this))
    }

    const optionButtons = root.querySelectorAll(".splitted-shipment-option")
    optionButtons.forEach((button) => {
      this.registerEventListener(button, "click", this.handleOptionSelect.bind(this))
    })

    const backdrop = root.querySelector(".splitted-shipment-prompt__backdrop")
    if (backdrop) {
      this.registerEventListener(backdrop, "click", this.handleBackdropClick.bind(this))
    }
  }

  // Render the component based on its current state
  render() {
    const classNames = {
      shown: this.data.show && "splitted-shipment-prompt--shown",
      hidden: !this.data.show && "splitted-shipment-prompt--hidden",
      loading: this.data.loading && "splitted-shipment-prompt--loading",
    }

    const className = Object.values(classNames).filter(Boolean).join(" ")

    this.renderContainer.innerHTML = /* html */ `
      <div class="splitted-shipment-prompt ${className}">
        <div class="splitted-shipment-prompt__backdrop">
          <div class="splitted-shipment-prompt__content">
            <button class="splitted-shipment-prompt__close" type="button" aria-label="Cerrar">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.9524 15.2938C15.9984 15.3367 16.0354 15.3884 16.061 15.4459C16.0866 15.5034 16.1004 15.5655 16.1015 15.6284C16.1026 15.6914 16.091 15.7539 16.0674 15.8123C16.0439 15.8706 16.0088 15.9237 15.9643 15.9682C15.9198 16.0127 15.8667 16.0478 15.8084 16.0714C15.75 16.0949 15.6875 16.1065 15.6245 16.1054C15.5616 16.1043 15.4995 16.0905 15.442 16.0649C15.3845 16.0393 15.3328 16.0023 15.2899 15.9563L9.99612 10.6633L4.70237 15.9563C4.61351 16.0391 4.49598 16.0842 4.37454 16.082C4.25311 16.0799 4.13724 16.0307 4.05136 15.9448C3.96547 15.8589 3.91628 15.743 3.91414 15.6216C3.91199 15.5002 3.95707 15.3826 4.03987 15.2938L9.33284 10L4.03987 4.70628C3.95707 4.61742 3.91199 4.49989 3.91414 4.37845C3.91628 4.25701 3.96547 4.14115 4.05136 4.05526C4.13724 3.96938 4.25311 3.92018 4.37454 3.91804C4.49598 3.9159 4.61351 3.96098 4.70237 4.04378L9.99612 9.33674L15.2899 4.04378C15.3787 3.96098 15.4963 3.9159 15.6177 3.91804C15.7391 3.92018 15.855 3.96938 15.9409 4.05526C16.0268 4.14115 16.076 4.25701 16.0781 4.37845C16.0802 4.49989 16.0352 4.61742 15.9524 4.70628L10.6594 10L15.9524 15.2938Z" fill="#08080A"/>
              </svg>
            </button>
            <p class="splitted-shipment-prompt__title">¿Cómo quieres recibir tu pedido?</p>
            <div class="splitted-shipment-prompt__options">
              ${this.renderShippingOptions()}
            </div>
            <button id="confirm" class="button button--full-width splitted-shipment-prompt__action" name="checkout" type="button">
              <span class="button__text">${this.data.loading ? "Procesando…" : "Continuar a pago seguro"}</span>
            </button>
          </div>
        </div>
      </div>
    `

    // Rebind events after rendering
    this.unbindEvents()
    this.bindEvents()
  }

  renderShippingOptions() {
    return [
      {
        key: "combined",
        title: "Envío conjunto",
        badge: "Más barato",
        description: `Ahorra gastos de envío recibiendo todo en un único envío a partir del <strong>${this.data.lastEstimatedShipmentDate}</strong>.`,
        icon: `
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.3307 5L7.9974 2L2.66406 5M13.3307 5V11L7.9974 14M13.3307 5L7.9974 8M7.9974 14L2.66406 11V5M7.9974 14V8M2.66406 5L7.9974 8M10.6641 3.5L5.33073 6.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `,
      },
      {
        key: "split",
        title: "Envíos separados",
        badge: null,
        description: `Te enviaremos los productos en cuanto estén listos. Cada envío tendrá su propia fecha y coste de envío.`,
        icon: `
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.66927 11L1.33594 9L4.66927 7L8.0026 9V12.6667L4.66927 14.6667V11Z" stroke="currentColor" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M1.33594 9.00016V12.6668L4.66927 14.6668M4.66927 11.0302L8.0026 9.01016M11.3359 11.0002L8.0026 9.00016M11.3359 11.0002V14.6668M11.3359 11.0002L14.6693 9.00016M8.0026 9.00016L11.3359 7.00016M8.0026 9.00016V5.3335M11.3359 7.00016L14.6693 9.00016M11.3359 7.00016V3.3335M14.6693 9.00016V12.6668L11.3359 14.6668M11.3359 14.6668L8.0026 12.6668M8.0026 5.3335L4.66927 3.3335L8.0026 1.3335L11.3359 3.3335M8.0026 5.3335L11.3359 3.3335M4.66927 3.3535V6.99016" stroke="currentColor" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `,
      },
    ]
      .map(
        (option) => /* html */ `
          <button
            type="button"
            class="splitted-shipment-option ${this.data.selectedShippingOption === option.key ? "is-active" : ""}"
            data-option-key="${option.key}"
          >
            <span class="splitted-shipment-option__icon" aria-hidden="true">${option.icon}</span>
            <span class="splitted-shipment-option__body">
              <span class="splitted-shipment-option__heading">
                <span class="splitted-shipment-option__title">${option.title}</span>
                ${option.badge ? `<span class="splitted-shipment-option__badge">${option.badge}</span>` : ""}
              </span>
              <span class="splitted-shipment-option__description">${option.description}</span>
            </span>
          </button>
        `
      )
      .join("")
  }
}

if (!window.customElements.get("splitted-shipment-prompt")) {
  window.customElements.define("splitted-shipment-prompt", SplittedShipmentPrompt)
}
